import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard as CreditCardIcon, Clock as ClockIcon, CheckCircle as CheckCircleIcon, XCircle as XCircleIcon, AlertTriangle as AlertTriangleIcon, HelpCircle as HelpCircleIcon, FileText as FileTextIcon, Smartphone as SmartphoneIcon } from 'lucide-react';
import { Button } from '../components/Button';
export function RefundPolicy() {
  return <main className="py-16 w-full bg-[#F9FAFB]" data-id="element-1219">
      <div className="container mx-auto px-4" data-id="element-1220">
        <div className="text-center mb-12" data-id="element-1221">
          <h1 className="text-4xl font-bold mb-4" data-id="element-1222">Refund Policy</h1>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1223">
            Our refund policy is designed to be fair and transparent. Please
            read it carefully to understand the conditions under which refunds
            are offered.
          </p>
        </div>
        {/* Important Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg mb-12" data-id="element-1224">
          <div className="flex" data-id="element-1225">
            <div className="flex-shrink-0" data-id="element-1226">
              <AlertTriangleIcon className="h-6 w-6 text-yellow-400" data-id="element-1227" />
            </div>
            <div className="ml-3" data-id="element-1228">
              <h3 className="text-lg font-medium text-yellow-800" data-id="element-1229">
                Important Notice for UAE Travelers
              </h3>
              <div className="mt-2 text-yellow-700" data-id="element-1230">
                <p className="mb-2" data-id="element-1231">
                  <strong data-id="element-1232">
                    You must activate your eSIM before arriving in the UAE.
                  </strong>
                </p>
                <p data-id="element-1233">
                  Due to local regulations, eSIMs for the UAE must be activated
                  before you arrive in the country. Failure to activate your
                  eSIM before arrival may result in connectivity issues, and
                  refunds will not be provided for this reason.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Refund Policy Overview */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-12" data-id="element-1234">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1235">Refund Policy Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1236">
            <div className="border border-gray-200 rounded-lg p-6" data-id="element-1237">
              <div className="flex items-center mb-4" data-id="element-1238">
                <div className="p-3 bg-green-100 rounded-lg mr-3" data-id="element-1239">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-1240" />
                </div>
                <h3 className="font-semibold text-lg" data-id="element-1241">Full Refund</h3>
              </div>
              <p className="text-gray-600" data-id="element-1242">
                Available for eSIMs that have not been activated. Must be
                requested within 30 days of purchase.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-6" data-id="element-1243">
              <div className="flex items-center mb-4" data-id="element-1244">
                <div className="p-3 bg-blue-100 rounded-lg mr-3" data-id="element-1245">
                  <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-1246" />
                </div>
                <h3 className="font-semibold text-lg" data-id="element-1247">Partial Refund</h3>
              </div>
              <p className="text-gray-600" data-id="element-1248">
                Available for activated eSIMs with less than 10% data usage
                within 24 hours of activation.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-6" data-id="element-1249">
              <div className="flex items-center mb-4" data-id="element-1250">
                <div className="p-3 bg-red-100 rounded-lg mr-3" data-id="element-1251">
                  <XCircleIcon className="h-6 w-6 text-red-600" data-id="element-1252" />
                </div>
                <h3 className="font-semibold text-lg" data-id="element-1253">No Refund</h3>
              </div>
              <p className="text-gray-600" data-id="element-1254">
                For eSIMs with more than 10% data usage or activated for more
                than 24 hours.
              </p>
            </div>
          </div>
          <p className="text-gray-600 mb-6" data-id="element-1255">
            At TravelSIM, we strive to provide excellent service and ensure your
            satisfaction with our eSIM products. We understand that
            circumstances may arise where you need to request a refund. Our
            refund policy is designed to be fair while also accounting for the
            digital nature of our products.
          </p>
          <p className="text-gray-600" data-id="element-1256">
            Please note that all refund requests are subject to review, and
            approval is at the discretion of TravelSIM. We reserve the right to
            modify this refund policy at any time, with changes effective upon
            posting to our website.
          </p>
        </div>
        {/* Refund Eligibility */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-12" data-id="element-1257">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1258">Refund Eligibility</h2>
          <div className="space-y-6" data-id="element-1259">
            <div className="border-b border-gray-200 pb-6" data-id="element-1260">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1261">
                Unopened/Unactivated eSIMs
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1262">
                If you have purchased an eSIM but have not yet activated it
                (i.e., you have not scanned the QR code or manually entered the
                activation details), you are eligible for a full refund within
                30 days of the purchase date.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1263">
                <div className="flex items-start" data-id="element-1264">
                  <div className="p-1 bg-green-100 rounded-full mr-3" data-id="element-1265">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-1266" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1267">
                    <strong data-id="element-1268">Example:</strong> You purchased an eSIM for your
                    trip but your plans changed, and you no longer need it.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-6" data-id="element-1269">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1270">
                Recently Activated eSIMs
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1271">
                If you have activated your eSIM but have used less than 10% of
                the included data and are within 24 hours of activation, you may
                be eligible for a partial refund (typically 50-75% of the
                purchase price, depending on usage).
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1272">
                <div className="flex items-start" data-id="element-1273">
                  <div className="p-1 bg-green-100 rounded-full mr-3" data-id="element-1274">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-1275" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1276">
                    <strong data-id="element-1277">Example:</strong> You activated your eSIM to test
                    it, but discovered your device isn't compatible.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-6" data-id="element-1278">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1279">Technical Issues</h3>
              <p className="text-gray-600 mb-4" data-id="element-1280">
                If you experience legitimate technical issues with your eSIM
                that our support team cannot resolve, you may be eligible for a
                full refund regardless of activation status. This is determined
                on a case-by-case basis after our technical team has attempted
                to resolve the issue.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1281">
                <div className="flex items-start" data-id="element-1282">
                  <div className="p-1 bg-green-100 rounded-full mr-3" data-id="element-1283">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-1284" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1285">
                    <strong data-id="element-1286">Example:</strong> Your eSIM doesn't connect to the
                    network despite your device being compatible and following
                    all troubleshooting steps.
                  </p>
                </div>
              </div>
            </div>
            <div data-id="element-1287">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1288">
                Non-Refundable Situations
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1289">
                Refunds are generally not available in the following situations:
              </p>
              <ul className="list-disc pl-5 mb-4 text-gray-600 space-y-2" data-id="element-1290">
                <li data-id="element-1291">eSIMs that have been activated for more than 24 hours</li>
                <li data-id="element-1292">eSIMs with more than 10% of the data allowance used</li>
                <li data-id="element-1293">
                  If you've used the service as intended but simply no longer
                  need it
                </li>
                <li data-id="element-1294">
                  If you failed to activate your UAE eSIM before arriving in the
                  UAE
                </li>
                <li data-id="element-1295">
                  If you purchased the wrong plan or for the wrong country
                  (please verify before purchasing)
                </li>
                <li data-id="element-1296">
                  If your device is not compatible with eSIM technology (please
                  check compatibility before purchasing)
                </li>
              </ul>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1297">
                <div className="flex items-start" data-id="element-1298">
                  <div className="p-1 bg-red-100 rounded-full mr-3" data-id="element-1299">
                    <XCircleIcon className="h-4 w-4 text-red-600" data-id="element-1300" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1301">
                    <strong data-id="element-1302">Example:</strong> You've used your eSIM for several
                    days of your trip and have consumed 30% of your data
                    allowance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* How to Request a Refund */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-12" data-id="element-1303">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1304">How to Request a Refund</h2>
          <div className="space-y-6" data-id="element-1305">
            <p className="text-gray-600" data-id="element-1306">
              To request a refund, please follow these steps:
            </p>
            <div className="flex items-start" data-id="element-1307">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1308">
                <span className="text-blue-600 font-medium" data-id="element-1309">1</span>
              </div>
              <div data-id="element-1310">
                <h3 className="font-semibold text-lg mb-2" data-id="element-1311">
                  Contact Customer Support
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-1312">
                  Email our customer support team at{' '}
                  <a href="mailto:support@travelsim.com" className="text-blue-600 hover:underline" data-id="element-1313">
                    support@travelsim.com
                  </a>{' '}
                  or use the contact form on our website.
                </p>
                <p className="text-gray-600" data-id="element-1314">
                  Include your order number, the email address used for
                  purchase, and the reason for your refund request.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-1315">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1316">
                <span className="text-blue-600 font-medium" data-id="element-1317">2</span>
              </div>
              <div data-id="element-1318">
                <h3 className="font-semibold text-lg mb-2" data-id="element-1319">
                  Provide Required Information
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-1320">
                  Our support team may request additional information to process
                  your refund, such as:
                </p>
                <ul className="list-disc pl-5 mb-2 text-gray-600" data-id="element-1321">
                  <li data-id="element-1322">Proof of purchase</li>
                  <li data-id="element-1323">Details about any technical issues experienced</li>
                  <li data-id="element-1324">Screenshots of error messages (if applicable)</li>
                  <li data-id="element-1325">
                    Information about your device model and operating system
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex items-start" data-id="element-1326">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1327">
                <span className="text-blue-600 font-medium" data-id="element-1328">3</span>
              </div>
              <div data-id="element-1329">
                <h3 className="font-semibold text-lg mb-2" data-id="element-1330">Review Process</h3>
                <p className="text-gray-600 mb-2" data-id="element-1331">
                  Our team will review your request within 1-2 business days. We
                  may contact you for additional information if needed.
                </p>
                <p className="text-gray-600" data-id="element-1332">
                  If your refund is approved, the amount will be credited back
                  to your original payment method. Please allow 5-10 business
                  days for the refund to appear in your account, depending on
                  your payment provider.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-8 p-6 bg-blue-50 rounded-lg" data-id="element-1333">
            <div className="flex items-start" data-id="element-1334">
              <div className="p-2 bg-blue-100 rounded-lg mr-4 flex-shrink-0" data-id="element-1335">
                <HelpCircleIcon className="h-6 w-6 text-blue-600" data-id="element-1336" />
              </div>
              <div data-id="element-1337">
                <h3 className="font-semibold text-lg mb-2" data-id="element-1338">Need Help?</h3>
                <p className="text-gray-600 mb-4" data-id="element-1339">
                  If you have any questions about our refund policy or need
                  assistance with a refund request, our support team is here to
                  help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1340">
                  <Link to="/contact" data-id="element-1341">
                    <Button variant="primary" data-id="element-1342">Contact Support</Button>
                  </Link>
                  <Button variant="outline" data-id="element-1343">Live Chat</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Special Considerations */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-12" data-id="element-1344">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1345">Special Considerations</h2>
          <div className="space-y-6" data-id="element-1346">
            <div className="border-b border-gray-200 pb-6" data-id="element-1347">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1348">
                UAE Activation Requirement
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1349">
                <strong data-id="element-1350">For UAE eSIMs:</strong> You must activate your eSIM
                before arriving in the UAE. This is a regulatory requirement,
                not just our policy. Failure to activate before arrival may
                result in connectivity issues, and refunds will not be provided
                for this reason.
              </p>
              <div className="bg-yellow-50 p-4 rounded-lg" data-id="element-1351">
                <div className="flex items-start" data-id="element-1352">
                  <div className="p-1 bg-yellow-100 rounded-full mr-3" data-id="element-1353">
                    <AlertTriangleIcon className="h-4 w-4 text-yellow-600" data-id="element-1354" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1355">
                    We strongly recommend activating your UAE eSIM at least 24
                    hours before your arrival.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-6" data-id="element-1356">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1357">
                Device Compatibility
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1358">
                It is the customer's responsibility to ensure their device is
                compatible with eSIM technology before purchasing. We provide a
                compatibility checker on our website, and our support team can
                assist with compatibility questions.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1359">
                <div className="flex items-start" data-id="element-1360">
                  <div className="p-1 bg-blue-100 rounded-full mr-3" data-id="element-1361">
                    <HelpCircleIcon className="h-4 w-4 text-blue-600" data-id="element-1362" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1363">
                    <Link to="/installation-guide" className="text-blue-600 hover:underline" data-id="element-1364">
                      Check our device compatibility list
                    </Link>{' '}
                    before purchasing to ensure your device supports eSIM
                    technology.
                  </p>
                </div>
              </div>
            </div>
            <div data-id="element-1365">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1366">Network Coverage</h3>
              <p className="text-gray-600 mb-4" data-id="element-1367">
                While we strive to provide excellent coverage through our
                partner networks, we cannot guarantee coverage in all areas.
                Network coverage can vary based on location, local
                infrastructure, and other factors beyond our control. Refunds
                based solely on coverage issues are evaluated on a case-by-case
                basis.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1368">
                <div className="flex items-start" data-id="element-1369">
                  <div className="p-1 bg-blue-100 rounded-full mr-3" data-id="element-1370">
                    <HelpCircleIcon className="h-4 w-4 text-blue-600" data-id="element-1371" />
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1372">
                    Check our coverage maps before purchasing to ensure service
                    availability in your destination.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Related Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1373">
          <Link to="/terms-privacy" className="bg-white rounded-xl shadow-sm p-6 hover:border-blue-200 border border-transparent transition-colors" data-id="element-1374">
            <div className="flex items-center mb-4" data-id="element-1375">
              <div className="p-3 bg-blue-100 rounded-lg mr-4" data-id="element-1376">
                <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-1377" />
              </div>
              <h3 className="font-semibold text-lg" data-id="element-1378">Terms of Service</h3>
            </div>
            <p className="text-gray-600" data-id="element-1379">
              Read our complete terms of service for detailed information about
              using our services.
            </p>
          </Link>
          <Link to="/installation-guide" className="bg-white rounded-xl shadow-sm p-6 hover:border-blue-200 border border-transparent transition-colors" data-id="element-1380">
            <div className="flex items-center mb-4" data-id="element-1381">
              <div className="p-3 bg-blue-100 rounded-lg mr-4" data-id="element-1382">
                <SmartphoneIcon className="h-6 w-6 text-blue-600" data-id="element-1383" />
              </div>
              <h3 className="font-semibold text-lg" data-id="element-1384">Installation Guide</h3>
            </div>
            <p className="text-gray-600" data-id="element-1385">
              Step-by-step instructions for installing and activating your eSIM
              on different devices.
            </p>
          </Link>
          <Link to="/contact" className="bg-white rounded-xl shadow-sm p-6 hover:border-blue-200 border border-transparent transition-colors" data-id="element-1386">
            <div className="flex items-center mb-4" data-id="element-1387">
              <div className="p-3 bg-blue-100 rounded-lg mr-4" data-id="element-1388">
                <HelpCircleIcon className="h-6 w-6 text-blue-600" data-id="element-1389" />
              </div>
              <h3 className="font-semibold text-lg" data-id="element-1390">Contact Support</h3>
            </div>
            <p className="text-gray-600" data-id="element-1391">
              Need help? Our support team is available 24/7 to assist you with
              any questions.
            </p>
          </Link>
        </div>
      </div>
    </main>;
}